/**
 * progressStatus — resolves live-status entries into translated sentences.
 *
 * One place owns the precedence + fallback rules for the persistent chat
 * status line: backend `progress` events carry a flat i18n key suffix
 * (`chat.progress.<key>`) plus interpolation params; unknown keys degrade
 * to a generic "Working on it…" (never a raw key). Phase labels are shared
 * with TypingIndicator so both surfaces stay wording-identical.
 */
import { bidiIsolate } from '@/utils/bidi';

export type ThinkingPhaseLit = 'uploading' | 'opening' | 'preparing' | 'working' | 'thinking';

/** One live-status update queued for the status line (latest-wins queue). */
export type LiveStatusEntry =
  | { kind: 'phase'; phase: ThinkingPhaseLit; filename?: string }
  | { kind: 'progress'; key: string; tool?: string; params?: Record<string, unknown> };

type TFunc = (key: string, options?: Record<string, unknown>) => string;

/**
 * A live number inside a status sentence. The backend reports the document
 * word count every ~2s; rendering the raw value made the counter jump
 * (20 → 70 → 140). The sentence carries the target as a marker and
 * LiveStatusLine counts up to it one number at a time, so the digits climb
 * the way a token counter does. The marker also tells the line to hide its
 * elapsed seconds — the count is the progress.
 */
export const LIVE_COUNT_MARKER = /\{\{count:(\d+)\}\}/;
export const countMarker = (n: number): string => `{{count:${Math.max(0, Math.floor(n))}}}`;

/** Status keys whose params carry a live count rather than a new sentence,
 *  mapped to the param that holds the count. The backend emits them while
 *  the model streams a long tool call or a long stretch of reasoning
 *  (caching_litellm._note_writing_progress / _note_reasoning_progress). */
const COUNTING_KEYS: Record<string, string> = {
  artifact_writing: 'words',
  artifact_editing: 'words',
  writingDriveChanges: 'kb',
  writingToolCall: 'kb',
  reasoningLong: 'words',
};

const countParam = (key: string): string | undefined =>
  Object.prototype.hasOwnProperty.call(COUNTING_KEYS, key) ? COUNTING_KEYS[key] : undefined;

/** Shared phase → sentence mapping (single source for TypingIndicator + status line). */
export function phaseLabel(t: TFunc, phase: ThinkingPhaseLit, filename?: string): string {
  switch (phase) {
    case 'uploading':
      return t('chat.thinking.uploading', {
        filename: filename || '',
        defaultValue: filename ? `Uploading ${filename}…` : 'Uploading…',
      });
    case 'opening':
      return t('chat.thinking.opening', {
        filename: filename || '',
        defaultValue: filename ? `Opening ${filename}…` : 'Opening…',
      });
    case 'preparing':
      return t('chat.thinking.preparing', {
        filename: filename || '',
        defaultValue: filename ? `Preparing ${filename}…` : 'Preparing…',
      });
    case 'working':
      return t('chat.thinking.working', { defaultValue: 'Working on it…' });
    case 'thinking':
    default:
      return t('chat.thinking.thinking', { defaultValue: 'Thinking…' });
  }
}

/** Resolve a live-status entry to its display sentence (called at render
 *  time so a mid-stream language switch re-resolves correctly). */
export function resolveLiveStatusLabel(t: TFunc, entry: LiveStatusEntry): string {
  if (entry.kind === 'phase') return phaseLabel(t, entry.phase, entry.filename);
  const fallback = entry.tool
    ? t('chat.progress.fallbackTool', {
        tool: entry.tool,
        defaultValue: `Working on it (${entry.tool})…`,
      })
    : t('chat.progress.fallback', { defaultValue: 'Working on it…' });
  const params: Record<string, unknown> = { ...(entry.params || {}) };
  // A web search query is the person's own words, in any script: isolate it
  // (FSI … PDI) so an English query inside the Hebrew sentence, or the
  // reverse, keeps the sentence's order.
  if (typeof params.query === 'string') params.query = bidiIsolate(params.query);
  // So is the site of a page being opened (a Latin host in a Hebrew line).
  if (typeof params.site === 'string') params.site = bidiIsolate(params.site);
  const countKey = countParam(entry.key);
  const counting = countKey !== undefined && typeof params[countKey] === 'number';
  if (counting) {
    // The number is animated by LiveStatusLine, not printed here.
    params[countKey] = countMarker(params[countKey] as number);
  }
  const resolved = t(`chat.progress.${entry.key}`, {
    ...params,
    tool: entry.tool,
    defaultValue: fallback,
    ...(counting ? { interpolation: { escapeValue: false } } : {}),
  });
  // A sentence whose interpolation params never arrived (e.g. an untitled
  // artifact) would show raw "{{title}}" — degrade to the fallback instead.
  const leftover = resolved.replace(LIVE_COUNT_MARKER, '');
  return leftover.includes('{{') ? fallback : resolved;
}

/** Same-entry check for the latest-wins queue: a re-announced identical
 *  status keeps its original `since`, which is what makes the elapsed
 *  counter tick up instead of resetting. */
export function liveStatusEntriesEqual(a: LiveStatusEntry, b: LiveStatusEntry): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'phase' && b.kind === 'phase') {
    return a.phase === b.phase && (a.filename ?? '') === (b.filename ?? '');
  }
  if (a.kind === 'progress' && b.kind === 'progress') {
    if (a.key !== b.key || (a.tool ?? '') !== (b.tool ?? '')) return false;
    // A counting status is the SAME status with a new number: keep it in
    // place (no cross-fade, no elapsed reset) and let the count climb.
    if (countParam(a.key) !== undefined) return true;
    return JSON.stringify(a.params ?? {}) === JSON.stringify(b.params ?? {});
  }
  return false;
}
